import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { fetchUsers, deleteUser } from "../../../redux/actions/userActions";
import { generatePasswordResetLink } from "../../../services/userService";
import {
  incrementLoading,
  decrementLoading,
} from "../../../redux/slices/loadingSlice";
import { logSystemEvent } from "../../../redux/slices/auditLogsSlice";
import * as XLSX from "xlsx";
import Pagination from "../../common/Pagination";
import AddOrEditUser from "./AddOrEditUser";
import IconButton from "../../common/Button";
import ChipStatus from "../../common/ChipStatus";
import { formatDate } from "../../../utils/dateUtils";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import Menu from "../../common/Menu";
import {
  SearchIcon,
  PlusIcon,
  SortArrowIcon,
  EditIcon,
  DeleteIcon,
  SyncIcon,
  ExportUpIcon,
} from "../../common/Icons";
import "../CarePlan/CarePlans.css";
import "./MDTUsers.css";
import UserDetail from "./UserDetail";
import { setFilters } from "../../../redux/slices/userSlice";
import { useDebounce } from "../../../hooks/useDebounce";

const MDTUsers = () => {
  const dispatch = useDispatch();

  // Selectors for users
  const users = useSelector((state) => state.users.users);
  const totalRows = useSelector((state) => state.users.userTotalRows);
  const userLoading = useSelector((state) => state.users.userLoading);
  const userError = useSelector((state) => state.users.userError);
  const currentPage = useSelector((state) => state.users.userCurrentPage);
  const rowsPerPage = useSelector((state) => state.users.userRowsPerPage);
  const userSearchQuery = useSelector((state) => state.users.userSearchQuery);

  // Local component states
  const [searchInput, setSearchInput] = useState("");
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [showResetLinkOverlay, setShowResetLinkOverlay] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Debounce the search input
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update filters when the debounced search input changes
  useEffect(() => {
    const newFilters = debouncedSearchInput
      ? { name: debouncedSearchInput, email: debouncedSearchInput }
      : { name: "", email: "" };

    // Only dispatch if the newFilters differ from the current searchQuery
    if (
      newFilters.name !== userSearchQuery.name ||
      newFilters.email !== userSearchQuery.email
    ) {
      dispatch(setFilters(newFilters));
    }
  }, [debouncedSearchInput, dispatch, userSearchQuery]);

  // Fetch users when filters (or other pagination/search parameters) update
  useEffect(() => {
    const ordering = sortField
      ? sortOrder === "asc"
        ? sortField
        : "-" + sortField
      : "";

    dispatch(
      fetchUsers({
        page: currentPage,
        rowsPerPage,
        searchQuery: userSearchQuery,
        ordering,
      })
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    userSearchQuery,
    sortField,
    sortOrder,
  ]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const data = users[currentPage] || [];
  // Calculate total pages
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const handlePageChange = (newPage) => {
    dispatch(
      fetchUsers({
        page: newPage,
        rowsPerPage,
        searchQuery: userSearchQuery,
      })
    );
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    dispatch(
      fetchUsers({
        page: 1, // reset to first page
        rowsPerPage: newRowsPerPage,
        searchQuery: userSearchQuery,
      })
    );
  };

  const handleResetPassword = async (user) => {
    try {
      const { reset_link } = await generatePasswordResetLink(user.id);
      setResetLink(reset_link);
      setShowResetLinkOverlay(true);
    } catch (err) {
      console.error("Error generating reset link:", err);
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteUser(selectedUser.id))
      .then(() => {
        setShowDeleteUser(false); // Close delete confirmation popup
        setShowEditUser(false); // Close the edit user modal as the user is removed
        setShowUserDetail(false); // Close the detail modal
        setSelectedUser({}); // Clear selected user to avoid stale data
      })
      .catch((err) => {
        console.error("Error deleting user:", err);
        setShowDeleteUser(false); // Close delete confirmation popup even on failure
      });
  };

  // When user clicks on the name/email cell
  const handleViewUserDetail = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  // If the user detail model is open and the user clicks "Reset password" from the menu,
  // close the user detail modal first, then open the reset password modal
  const handleResetPasswordClick = (user) => {
    // Close the user details modal first
    setShowUserDetail(false);
    // Then, call your reset password function (which sets the reset password modal visible)
    handleResetPassword(user);
  };

  const handleMenuClick = (user, action) => {
    setSelectedUser(user); // Set the selected care plan
    if (action === "edit") setShowEditUser(true);
    // if (action === "addUser") setAddMdtUser(true);
    if (action === "delete") setShowDeleteUser(true);
    // if (action === "publish") setShowPublishConfirmModal(true);
  };

  // → export all pages of users as Excel
  const handleExport = async () => {
    dispatch(incrementLoading()); // Start loading
    // preserve your current ordering/search
    const ordering = sortField
      ? sortOrder === "asc"
        ? sortField
        : `-${sortField}`
      : "";

    try {
      // 1) fetch _all_ users in one go
      const action = await dispatch(
        fetchUsers({
          page: 1,
          rowsPerPage: totalRows, // ← ask backend for every user
          searchQuery: userSearchQuery,
          ordering,
        })
      );
      const payload = unwrapResult(action);
      const allUsers = payload.data ?? payload;

      // 2) shape data & export
      const exportData = allUsers.map((u) => ({
        Name: u.name,
        Email: u.email,
        "MDT role": Array.isArray(u.mdt_roles)
          ? u.mdt_roles.join(", ")
          : u.mdt_roles,
        "Active access": u.is_active_officer ? "Yes" : "No",
        "Is Admin": u.is_admin ? "Yes" : "No",
        "Date registered": u.registered ? formatDate(u.registered) : "",
        "Last active": u.last_active ? formatDate(u.last_active) : "",
        "Date added": u.date_added ? formatDate(u.date_added) : "",
        "Added by": u.added_by || "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      XLSX.writeFile(wb, "user_list.xlsx");

      // log the export action
      dispatch(logSystemEvent("User list exported"));
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      dispatch(decrementLoading()); // Stop loading
    }
  };

  // Popup Menu Items
  const getMenuItems = (row) => [
    {
      label: "Edit Details",
      icon: <EditIcon />,
      action: () => handleMenuClick(row, "edit"),
      // action: () => {
      //   setViewCarePlanId(row.id); // Assuming 'id' is the carePlanId
      // },
    },
    {
      label: "Reset password",
      icon: <SyncIcon color="#6C757D" />,
      action: () => handleResetPassword(row),
    },
    {
      label: "Delete User",
      icon: <DeleteIcon />,
      action: () => handleMenuClick(row, "delete"),
      // action: () => setEditCarePlanNHS(row.nhsNumber),
    },
  ];

  if (userLoading) {
    return <div>Loading...</div>;
  }

  if (userError) {
    return <div>Error: {userError}</div>;
  }

  return (
    <>
      <div className="draftplan-container">
        <div className="search-filter-bar">
          <div className="left-section">
            <div className="search-bar">
              <div className="search-icon">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Search user"
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div className="right-section">
            <IconButton
              variant="secondary"
              state="idle"
              type="button"
              icon={<ExportUpIcon color="#004F96" />}
              onClick={handleExport}
            >
              Export list
            </IconButton>
            <IconButton
              variant="primary"
              state="idle"
              icon={<PlusIcon color="#FFF" />}
              type="button"
              width={"auto"}
              onClick={() => setShowAddUser(true)}
            >
              Add user
            </IconButton>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="care-plan-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("profile__name")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    Name
                    <span className="header-icon">
                      {sortField === "name" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("profile__mdt_roles__name")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    Job Role
                    <span className="header-icon">
                      {sortField === "profile__mdt_roles__name" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("profile__is_admin")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    Admin
                    <span className="header-icon">
                      {sortField === "profile__is_admin" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>

                <th
                  onClick={() => handleSort("profile__is_active_officer")}
                  style={{ cursor: "pointer" }}
                  className="max-width-40"
                >
                  <div className="header-content">
                    Active access
                    <span className="header-icon">
                      {sortField === "profile__is_active_officer" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th className="hide-on-mobile">
                  <div
                    className="header-content"
                    onClick={() => handleSort("profile__registered")}
                    style={{ cursor: "pointer" }}
                  >
                    Registered
                    <span className="header-icon">
                      {sortField === "profile__registered" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("profile__last_active")}
                  style={{ cursor: "pointer" }}
                  className="hide-on-mobile"
                >
                  <div className="header-content">
                    Last active
                    <span className="header-icon">
                      {sortField === "profile__last_active" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                {/* SHow the empty header for menu dots */}
                <th></th>
                {/* Add more columns as needed */}
              </tr>
            </thead>
            <tbody>
              {data.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div
                      style={{ cursor: "pointer", display: "inline-block" }}
                      onClick={() => handleViewUserDetail(user)}
                    >
                      <div className="body-text-regular medium neutral-900">
                        {user.name}
                      </div>
                      {/* <div className="body-text-small regular neutral-700">
                        {user.email}
                      </div> */}
                    </div>
                  </td>
                  <td>{user.mdt_roles}</td>
                  <td>{user.is_admin ? "Yes" : "No"}</td>
                  <td className="max-width-40">
                    {user.is_active_officer ? "Yes" : "No"}
                  </td>
                  <td className="hide-on-mobile">
                    {user.registered ? formatDate(user.registered) : ""}
                  </td>
                  <td className="hide-on-mobile">
                    {user.last_active ? formatDate(user.last_active) : ""}
                  </td>
                  <td>
                    <span className="draftplan-menu-icon">
                      <Menu menuItems={getMenuItems(user)} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />

        {showAddUser && (
          <AddOrEditUser
            onClose={() => setShowAddUser(false)}
            isOpen={showAddUser}
          />
        )}
        {showEditUser && (
          <AddOrEditUser
            onClose={() => setShowEditUser(false)}
            isOpen={showEditUser}
            mode="edit"
            initialValues={selectedUser}
            onDeleteUser={() => {
              setShowDeleteUser(true);
            }}
          />
        )}

        {/* Confirm Delete Care Plan Popup */}
        {showDeleteUser && (
          <FullScreenOverlay
            zIndex={1100}
            width="480px"
            height="300px"
            isPopup={true}
            popupType="delete"
            buttons={[
              {
                label: "No, Cancel",
                onClick: () => {
                  setShowDeleteUser(false);
                },
              },
              {
                label: "Yes, Delete",
                onClick: handleConfirmDelete, // Handle delete confirmation
                variant: "danger",
                state: "idle",
              },
            ]}
          >
            <h5 className="semi-bold">Want to delete this user?</h5>
            <p>
              Are you sure you want to delete this user from this platform? By
              proceeding you will lose all the data attributed as they will be
              permanently removed.
            </p>
          </FullScreenOverlay>
        )}

        {/* Reset Password Link Overlay */}
        {showResetLinkOverlay && (
          <FullScreenOverlay
            zIndex={1100}
            width="480px"
            height="300px"
            isPopup={true}
            popupType="save"
            buttons={[
              {
                label: "Close",
                onClick: () => {
                  setShowResetLinkOverlay(false);
                  setResetLink("");
                  setShowCopiedTooltip(false);
                },
              },
              {
                label: "Copy URL",
                onClick: () => {
                  navigator.clipboard.writeText(resetLink);
                  setShowCopiedTooltip(true);
                },
                variant: "primary",
                state: "idle",
              },
            ]}
          >
            <h5 className="semi-bold">Password reset</h5>
            <p>
              Copy the password reset URL and share the lin with the user so
              they can reset their password
            </p>
            {showCopiedTooltip && (
              <ChipStatus autoDismiss={false} status="success">
                The URL is copied successfully
              </ChipStatus>
            )}
          </FullScreenOverlay>
        )}

        {/* Render the detail modal if showUserDetail is true */}
        {showUserDetail && (
          <UserDetail
            isOpen={showUserDetail}
            onClose={() => setShowUserDetail(false)}
            user={selectedUser}
            // Re-use your existing delete/reset logic:
            onRemoveUser={(user) => {
              // Option A: Show your delete confirmation
              setShowDeleteUser(true);
              setSelectedUser(user);
              // or handle immediate removal if desired
            }}
            onResetPassword={(user) => handleResetPasswordClick(user)}
          />
        )}
      </div>
    </>
  );
};

export default MDTUsers;
